import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Container,
  InputAdornment,
  Link,
  Skeleton,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { ClientOrder, Contact } from "../types/ClientOrder";
import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DateRange, DateRangePicker, SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { fMonthDayYearSlashNotation } from "../../utils/formatTime";
import { fCurrency } from "../../utils/formatNumber";
import ClientOrdersLoadingSkeleton from "./ClientOrdersLoadingSkeleton";
import Iconify from "../../../minimals/components/iconify";
import { debounce } from "lodash";
import { InternalUser } from "../../types/app";
import SalesPersonAutocomplete from "./SalesPersonAutocomplete";
import StickyTable from "../../shared/StickyTable";
import MultiUpdateModal from "./MultiUpdateModal";

const today = dayjs();
const defaultEndDate = today;
const defaultStartDate = today.subtract(2, "weeks");

const getChipColor = (transactionType: string) => {
  switch (transactionType) {
    case "Purchase":
      return "info";
    case "Refinance":
      return "warning";
    default:
      return "default";
  }
};

type SortField = "fileNumber" | "amount" | "salesRep" | "closed" | null;
type SortOrder = "asc" | "desc";

export default function ClientOrdersNew() {
  const RECENT_ORDERS_KEY = "recentOrderNumbers";
  const [clientOrders, setClientOrders] = useState<ClientOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<ClientOrder[]>([]);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([defaultStartDate, defaultEndDate]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [filter, setFilter] = useState<string>("Missing Deal Owners");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>("Commercial");
  const [statusFilter, setStatusFilter] = useState<string>("Closed");
  const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
  const [loadingOrderNumbers, setLoadingOrderNumbers] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField>("closed");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [selectedOrders, setSelectedOrders] = useState<ClientOrder[]>([]);
  const [multiUpdateOpen, setMultiUpdateOpen] = useState(false);
  const [modalSaveLoading, setModalSaveLoading] = useState(false);
  const [allContactsByOrder, setAllContactsByOrder] = useState<Record<string, Contact[]>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

  function getRecentOrders(): string[] {
    const stored = localStorage.getItem(RECENT_ORDERS_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  function addRecentOrder(orderNumber: string) {
    let recent = getRecentOrders();
    recent = recent.filter((o) => o !== orderNumber);
    recent.unshift(orderNumber);
    if (recent.length > 5) recent = recent.slice(0, 5);
    localStorage.setItem(RECENT_ORDERS_KEY, JSON.stringify(recent));
  }

  const searchClientOrders = async (): Promise<void> => {
    const params = orderNumber
      ? { orderNumber }
      : {
          from: dateRange[0]?.toDate(),
          to: dateRange[1]?.toDate(),
          orderNumber,
          commercial: transactionTypeFilter === "Commercial",
          status: statusFilter
        };
    setLoading(true);

    const { data } = await axios.get<ClientOrder[]>("/api/orders/getClientOrders", { params });
    const sortedOrders = data.sort((a, b) => {
      const dateA = new Date(a.closingDate);
      const dateB = new Date(b.closingDate);
      return dateA.getTime() - dateB.getTime();
    });

    setClientOrders(sortedOrders);
    triggerFilter(sortedOrders);
    setLoading(false);
  };

  const triggerFilter = (orders: ClientOrder[]) => {
    let filtered = orders;
    if (!orderNumber) {
      switch (filter) {
        case "Missing Deal Owners":
          filtered = orders.filter(
            (o) => (!o.dealOwnerLookupCodes || o.dealOwnerLookupCodes.length === 0) && o.fulfill
          );
          break;
        case "Contains Deal Owners":
          filtered = orders.filter((o) => o.dealOwnerLookupCodes?.length > 0);
          break;
        case "Active":
          filtered = orders.filter((o) => o.fulfill);
          break;
        case "Inactive":
          filtered = orders.filter((o) => !o.fulfill);
          break;
        case "Power BI Ignored":
          filtered = orders.filter((o) => !o.reporting);
          break;
        default:
          filtered = orders;
      }
    }

    setFilteredOrders(filtered);
    setPageNumber(1);
  };

  const setPrimaryContactOnOrder = async (clientOrder: ClientOrder) => {
    const payload = {
      orderNumber: clientOrder.number,
      lookupCodes: clientOrder.dealOwnerLookupCodes
    };
    await axios.post<ClientOrder[]>("/api/orders/setClientOnOrder", payload);
  };
  const handleSalesRepsChange = async (clientOrder: ClientOrder, users: InternalUser[]) => {
    setLoading(true);
    const salesRepIds = users.map((u) => u.salesRepId);
    await axios.post("/proxy/api/orders/setsalesreps", {
      orderNumber: clientOrder.number,
      salesRepIds
    });
    updateOrdersSalesPersons(clientOrder, users);
    setLoading(false);
  };

  const handleMultiUpdate = async (
    orders: ClientOrder[],
    selectedContacts: Contact[] | null,
    salesReps: InternalUser[] | null
  ) => {
    setModalSaveLoading(true);
    for (const order of orders) {
      if (selectedContacts?.length && salesReps?.length) {
        await handleContactChange(order, selectedContacts);
        await handleSalesRepsChange(order, salesReps);
      } else if (selectedContacts?.length) {
        await handleContactChange(order, selectedContacts);
      } else if (salesReps?.length) {
        await handleSalesRepsChange(order, salesReps);
      }
    }
    await searchClientOrders();
    setModalSaveLoading(false);
    setMultiUpdateOpen(false);
    setSelectedOrders([]);
  };

  const setFulfillOnOrder = (clientOrder: ClientOrder) => {
    const payload = {
      orderNumber: clientOrder.number,
      value: clientOrder.fulfill
    };
    axios.post<ClientOrder[]>("/api/orders/setFulfillOnOrder", payload);
  };

  const handleContactChange = (clientOrder: ClientOrder, contacts: Contact[]) => {
    const updatedOrder = {
      ...clientOrder,
      dealOwnerLookupCodes: contacts.map((c) => c.lookupCode)
    } as ClientOrder;
    const updatedOrders = clientOrders.map((o) => {
      if (o.number === clientOrder.number) {
        return updatedOrder;
      } else {
        return o;
      }
    });
    setClientOrders(updatedOrders);
    setPrimaryContactOnOrder(updatedOrder);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedOrders = useMemo(() => {
    if (!sortField) return filteredOrders;

    return [...filteredOrders].sort((a, b) => {
      let compareResult = 0;

      switch (sortField) {
        case "fileNumber":
          compareResult = a.number.localeCompare(b.number);
          break;
        case "amount":
          const amountA = a.transactionType === "Refinance" ? a.loanAmount : a.purchasePrice;
          const amountB = b.transactionType === "Refinance" ? b.loanAmount : b.purchasePrice;
          compareResult = amountA - amountB;
          break;
        case "salesRep":
          const salesRepA = a.salesReps || "";
          const salesRepB = b.salesReps || "";
          compareResult = salesRepA.localeCompare(salesRepB);
          break;
        case "closed":
          const dateA = new Date(a.closingDate).getTime();
          const dateB = new Date(b.closingDate).getTime();
          compareResult = dateA - dateB;
          break;
      }

      return sortOrder === "asc" ? compareResult : -compareResult;
    });
  }, [filteredOrders, sortField, sortOrder]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedOrders.slice(startIndex, endIndex);
  }, [sortedOrders, pageNumber, pageSize]);

  const debouncedOrderSearchTextChange = debounce(async (text: string | null) => {
    if (!text) {
      setOrderNumbers([]);
      return;
    }
    setLoadingOrderNumbers(true);
    const { data } = await axios.get(`/proxy/api/orders/findRelatedOrdersByNumber?searchText=${text}`);
    setOrderNumbers(data);
    setLoadingOrderNumbers(false);
  }, 300);

  const findContact = (contacts: Contact[], lookupCodes: string[]) => {
    return contacts.filter((contact) => lookupCodes?.find((c) => c === contact.lookupCode));
  };

  const setReportingOnOrder = (clientOrder: ClientOrder) => {
    const payload = {
      orderNumber: clientOrder.number,
      value: clientOrder.reporting
    };
    axios.post<ClientOrder[]>("/api/orders/setReportingOnOrder", payload);
  };

  const handleFulfillChange = (clientOrder: ClientOrder, checked: boolean) => {
    const updatedOrder = {
      ...clientOrder,
      fulfill: checked
    } as ClientOrder;
    const updatedOrders = clientOrders.map((o) => {
      if (o.number === clientOrder.number) {
        return updatedOrder;
      } else {
        return o;
      }
    });
    setClientOrders(updatedOrders);
    setFulfillOnOrder(updatedOrder);
    triggerFilter(updatedOrders);
  };

  const handleReportingChange = (clientOrder: ClientOrder, checked: boolean) => {
    const updatedOrder = {
      ...clientOrder,
      reporting: checked
    } as ClientOrder;
    const updatedOrders = clientOrders.map((o) => {
      if (o.number === clientOrder.number) {
        return updatedOrder;
      } else {
        return o;
      }
    });
    setClientOrders(updatedOrders);
    setReportingOnOrder(updatedOrder);
    triggerFilter(updatedOrders);
  };

  const updateOrdersSalesPersons = (clientOrder: ClientOrder, salesReps: InternalUser[]) => {
    const updatedOrders = clientOrders.map((o) => {
      if (o.number === clientOrder.number) {
        return { ...o, salesReps: salesReps.map((r) => r.fullName).join(",") };
      } else {
        return o;
      }
    });
    setClientOrders(updatedOrders);
  };
  const toggleContacts = (row: any) => {
    if (expandedRows[row.number]) {
      setExpandedRows((prev) => ({
        ...prev,
        [row.number]: false
      }));
    } else {
      loadAllContacts(row);
    }
  };
  const loadAllContacts = async (row: any) => {
    const { data } = await axios.get(`/proxy/api/orders/GetOrderContacts?orderNumber=${row.number}`);

    setAllContactsByOrder((prev) => ({
      ...prev,
      [row.number]: data.map((c: any) => ({
        ...c,
        contactType: c.contactType ?? c.type
      }))
    }));
    setExpandedRows((prev) => ({
      ...prev,
      [row.number]: true
    }));
  };

  const toggleRow = (order: ClientOrder) => {
    setSelectedOrders((prev) =>
      prev.some((o) => o.number === order.number) ? prev.filter((o) => o.number !== order.number) : [...prev, order]
    );
  };

  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }
    searchClientOrders();
  }, [dateRange, orderNumber, transactionTypeFilter, statusFilter]);

  useEffect(() => {
    triggerFilter(clientOrders);
  }, [filter]);

  return (
    <>
      <Helmet>
        <title>File Deal Owner | TitleQ</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" spacing={2} marginBottom={2}>
          <Typography variant="h4" marginBottom={2} sx={{ textWrap: "nowrap" }}>
            File Deal Owner
          </Typography>
          <Autocomplete
            options={orderNumbers.length ? orderNumbers : getRecentOrders()}
            getOptionLabel={(option) => option}
            groupBy={(option) => (orderNumbers.length === 0 ? "Recently Searched" : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ width: 280 }}
                placeholder="Search Order Number"
                onChange={(e) => debouncedOrderSearchTextChange(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon={"eva:search-fill"} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                    </InputAdornment>
                  )
                }}
              />
            )}
            onChange={async (e, val) => {
              if (val) {
                setOrderNumber(val);
                addRecentOrder(val);
              } else {
                setOrderNumber(null);
              }
            }}
            loading={loadingOrderNumbers}
          />
          <DateRangePicker
            onChange={(value) => setDateRange(value)}
            calendars={3}
            slots={{ field: SingleInputDateRangeField }}
            sx={{ width: 260 }}
            defaultValue={dateRange}
          />
          <Autocomplete
            options={["Missing Deal Owners", "Contains Deal Owners", "Active", "Inactive", "All", "Power BI Ignored"]}
            renderInput={(params) => <TextField {...params} />}
            sx={{ width: 250 }}
            value={filter}
            onChange={(e, v) => setFilter(v ? v : "Missing Deal Owners")}
          />
          <Autocomplete
            options={["Commercial", "Residential"]}
            renderInput={(params) => <TextField {...params} />}
            sx={{ width: 250 }}
            value={transactionTypeFilter}
            onChange={(e, v) => setTransactionTypeFilter(v ? v : "Commercial")}
          />
          <Autocomplete
            options={["Closed", "Open", "All"]}
            renderInput={(params) => <TextField {...params} />}
            sx={{ width: 250 }}
            value={statusFilter}
            onChange={(e, v) => setStatusFilter(v ? v : "Closed")}
          />
        </Stack>

        {loading && <ClientOrdersLoadingSkeleton />}

        {!loading && (
          <>
            <TableContainer>
              <StickyTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {selectedOrders.length > 0 && (
                        <Button size="small" sx={{ fontSize: 10 }} onClick={() => setMultiUpdateOpen(true)}>
                          Multi Update
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === "fileNumber"}
                        direction={sortField === "fileNumber" ? sortOrder : "asc"}
                        onClick={() => handleSort("fileNumber")}
                      >
                        File
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Transaction</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === "amount"}
                        direction={sortField === "amount" ? sortOrder : "asc"}
                        onClick={() => handleSort("amount")}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === "closed"}
                        direction={sortField === "closed" ? sortOrder : "asc"}
                        onClick={() => handleSort("closed")}
                      >
                        Closed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel>Settlement Date</TableSortLabel>
                    </TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Deal Owner</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === "salesRep"}
                        direction={sortField === "salesRep" ? sortOrder : "asc"}
                        onClick={() => handleSort("salesRep")}
                      >
                        Sales Person
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Power BI</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((row) => {
                    const contactsToShow = (
                      (expandedRows[row.number] ? allContactsByOrder[row.number] : row.contacts) || []
                    ).filter((c) => c?.name?.trim() && c?.lookupCode);
                    return (
                      <TableRow key={row.number}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedOrders.some((o) => o.number === row.number)}
                            onChange={() => toggleRow(row)}
                          />
                        </TableCell>
                        <TableCell width={120}>
                          <Link href={`${momentumUrl}new-home/${row.number}`} target="_blank">
                            {row.number}
                          </Link>
                          {loading && <Skeleton />}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.transactionType} color={getChipColor(row.transactionType)} size="small" />
                        </TableCell>
                        <TableCell>
                          {row.transactionType === "Refinance"
                            ? fCurrency(row.loanAmount)
                            : fCurrency(row.purchasePrice)}
                        </TableCell>
                        <TableCell>
                          {/* Handle default date returned when null */}
                          {row.closingDate.toString() === "0001-01-01T00:00:00"
                            ? "--"
                            : fMonthDayYearSlashNotation(row.closingDate)}
                        </TableCell>
                        <TableCell>
                          {row.settlementDate.toString() === "0001-01-01T00:00:00"
                            ? "--"
                            : fMonthDayYearSlashNotation(row.settlementDate)}
                        </TableCell>
                        <TableCell>{row.state}</TableCell>
                        <TableCell width={480}>
                          <Autocomplete
                            multiple
                            fullWidth
                            size="medium"
                            options={contactsToShow}
                            getOptionLabel={(option) =>
                              `${option.contactType} - ${option.name} ${`${option.isMarketingSource ? "⭐" : ""}`}`
                            }
                            renderInput={(params) => <TextField {...params} placeholder="Select Contact" />}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.onClick?.(e);
                                }}
                              >
                                {option.contactType ? `${option.contactType} - ` : ""}
                                {option.name} {option.isMarketingSource ? "⭐" : ""}
                              </li>
                            )}
                            ListboxProps={{
                              sx: {
                                "& ul": { position: "relative" },
                                "&::after": {
                                  content: expandedRows[row.number] ? '"Close all contacts…"' : '"See all contacts…"',
                                  display: "block",
                                  padding: "8px 16px",
                                  cursor: "pointer",
                                  backgroundColor: "#f5f5f5",
                                  fontWeight: 600
                                }
                              },
                              onClick: () => toggleContacts(row)
                            }}
                            onChange={(event, value) => handleContactChange(row, value)}
                            defaultValue={findContact(row.contacts, row?.dealOwnerLookupCodes)}
                          />
                        </TableCell>
                        <TableCell width={480}>
                          <SalesPersonAutocomplete clientOrder={row} update={updateOrdersSalesPersons} />
                        </TableCell>
                        <TableCell width={50}>
                          <Switch checked={row.fulfill} onChange={(e, checked) => handleFulfillChange(row, checked)} />
                        </TableCell>
                        <TableCell align="right" width={100}>
                          <Switch
                            checked={row.reporting}
                            onChange={(e, checked) => handleReportingChange(row, checked)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </StickyTable>
            </TableContainer>
            {multiUpdateOpen && (
              <MultiUpdateModal
                openModal={multiUpdateOpen}
                setOpenModal={setMultiUpdateOpen}
                clientOrders={selectedOrders}
                handleMultiUpdate={handleMultiUpdate}
                modalSaveLoading={modalSaveLoading}
              />
            )}
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={pageNumber - 1}
              onPageChange={(e, page) => setPageNumber(page + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[50, 100, 150]}
            />
          </>
        )}
      </Container>
    </>
  );
}
